import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import StatusBadge from '../../components/shared/StatusBadge';

const Submitted = () => {
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSubmission();
  }, []);

  const fetchSubmission = async () => {
    try {
      const response = await api.get('/submissions/my');
      if (response.data.success) {
        setSubmission(response.data.data.submission);
      }
    } catch (error) {
      console.error('Failed to fetch submission:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResubmit = () => {
    navigate('/checklist');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const isResubmissionRequired = submission?.status === 'requires_resubmission';
  const isApproved = submission?.status === 'approved';
  const isRejected = submission?.status === 'rejected';

  return (
    <div className="max-w-2xl mx-auto text-center">
      <div className="bg-white rounded-lg shadow-lg p-8">
        {isResubmissionRequired ? (
          <>
            <div className="w-20 h-20 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>

            <h1 className="text-3xl font-bold text-dark mb-4">Resubmission Required</h1>

            <p className="text-grey mb-6">
              Your submission requires changes based on the inspector's feedback. Please review the comments and update your submission accordingly.
            </p>

            <div className="bg-orange-50 rounded-lg p-6 mb-6 border border-orange-200">
              <h3 className="font-semibold text-dark mb-3">Inspector Notes</h3>
              <p className="text-left text-grey whitespace-pre-wrap">
                {submission?.inspector_notes || 'No specific notes provided. Please review the section comments for details.'}
              </p>
            </div>

            <button
              onClick={handleResubmit}
              className="inline-block bg-primary text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700 transition-colors"
            >
              Update and Resubmit
            </button>
          </>
        ) : isApproved ? (
          <>
            <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>

            <h1 className="text-3xl font-bold text-dark mb-4">Submission Approved</h1>

            <p className="text-grey mb-6">
              Congratulations! Your broker inspection checklist has been approved.
            </p>

            <div className="bg-green-50 rounded-lg p-6 mb-6 border border-green-200">
              <h3 className="font-semibold text-dark mb-3">Current Status</h3>
              <StatusBadge status="approved" />
            </div>

            <Link
              to="/checklist"
              className="inline-block bg-primary text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700 transition-colors"
            >
              View Your Submission
            </Link>
          </>
        ) : isRejected ? (
          <>
            <div className="w-20 h-20 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>

            <h1 className="text-3xl font-bold text-dark mb-4">Submission Rejected</h1>

            <p className="text-grey mb-6">
              Your submission has been rejected. Please contact the LuSE team for more information.
            </p>

            <div className="bg-red-50 rounded-lg p-6 mb-6 border border-red-200">
              <h3 className="font-semibold text-dark mb-3">Inspector Notes</h3>
              <p className="text-left text-grey whitespace-pre-wrap">
                {submission?.inspector_notes || 'No specific notes provided.'}
              </p>
            </div>

            <Link
              to="/checklist"
              className="inline-block bg-primary text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700 transition-colors"
            >
              View Your Submission
            </Link>
          </>
        ) : (
          <>
            <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>

            <h1 className="text-3xl font-bold text-dark mb-4">Submission Submitted Successfully</h1>

            <p className="text-grey mb-6">
              Your broker inspection checklist has been submitted and is now under review. You will be notified once the review is complete.
            </p>

            <div className="bg-offWhite rounded-lg p-6 mb-6">
              <h3 className="font-semibold text-dark mb-3">What happens next?</h3>
              <ul className="text-left text-grey space-y-2">
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Your submission will be reviewed by LuSE inspectors</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>You will receive an email notification when the review is complete</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>If resubmission is required, you will be notified of the changes needed</span>
                </li>
              </ul>
            </div>

            <Link
              to="/checklist"
              className="inline-block bg-primary text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700 transition-colors"
            >
              View Your Submission
            </Link>
          </>
        )}
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex flex-col items-center">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Need Help?</h3>
        <p className="text-gray-600 text-sm mb-4">Contact the LuSE team if you have any questions regarding your submission.</p>
        <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
          <a href="mailto:legal@luse.co.zm" className="flex items-center text-sm font-medium text-gray-700 bg-gray-50 px-4 py-2 rounded-md hover:bg-gray-100 transition-colors border border-gray-200">
            <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
            Legal: legal@luse.co.zm
          </a>
          <a href="mailto:itluse@luse.co.zm" className="flex items-center text-sm font-medium text-gray-700 bg-gray-50 px-4 py-2 rounded-md hover:bg-gray-100 transition-colors border border-gray-200">
            <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" /></svg>
            IT: itluse@luse.co.zm
          </a>
        </div>
      </div>
    </div>
  );
};

export default Submitted;
